import type { SortDescriptor } from "react-stately";

import { useCallback, useState } from "react";

type DesignerSectionType =
  | "awaiting-reply"
  | "being-revised"
  | "in-progress"
  | "others"
  | "ready-for-review";

interface SectionState {
  currentPage: number;
  itemsPerPage: number;
  sortDescriptor: SortDescriptor;
}

const DEFAULT_ITEMS_PER_PAGE = 10;

/**
 * Default sort configurations per section based on acceptance criteria:
 * - In Progress: Sort by due date (ascending, then createdAt desc)
 * - Being Revised: Sort by due date (ascending)
 * - Awaiting Reply: Sort by when added to status (newest first) = updatedAt desc
 * - Ready for Review: Sort by when added to status (newest first) = updatedAt desc
 * - Others: Sort by status (ascending)
 */
const SECTION_SORT_DEFAULTS: Record<
  DesignerSectionType,
  { column: string; direction: "ascending" | "descending" }
> = {
  "awaiting-reply": {
    column: "updatedAt",
    direction: "descending",
  },
  "being-revised": {
    column: "dueDate",
    direction: "ascending",
  },
  "in-progress": {
    column: "dueDate",
    direction: "ascending",
  },
  others: {
    column: "status",
    direction: "ascending",
  },
  "ready-for-review": {
    column: "updatedAt",
    direction: "descending",
  },
};

export function useDesignerSectionStates() {
  const [sectionStates, setSectionStates] = useState<
    Record<DesignerSectionType, SectionState>
  >({
    "awaiting-reply": {
      currentPage: 1,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      sortDescriptor: SECTION_SORT_DEFAULTS["awaiting-reply"],
    },
    "being-revised": {
      currentPage: 1,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      sortDescriptor: SECTION_SORT_DEFAULTS["being-revised"],
    },
    "in-progress": {
      currentPage: 1,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      sortDescriptor: SECTION_SORT_DEFAULTS["in-progress"],
    },
    others: {
      currentPage: 1,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      sortDescriptor: SECTION_SORT_DEFAULTS.others,
    },
    "ready-for-review": {
      currentPage: 1,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      sortDescriptor: SECTION_SORT_DEFAULTS["ready-for-review"],
    },
  });

  const createSectionHandlers = useCallback(
    (section: DesignerSectionType) => ({
      onItemsPerPageChange: (itemsPerPage: number) => {
        setSectionStates((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            currentPage: 1, // Reset to first page when changing items per page
            itemsPerPage,
          },
        }));
      },
      onPageChange: (page: number) => {
        setSectionStates((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            currentPage: page,
          },
        }));
      },
      onSortChange: (sortDescriptor: SortDescriptor) => {
        setSectionStates((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            sortDescriptor,
          },
        }));
      },
    }),
    [],
  );

  return {
    createSectionHandlers,
    sectionStates,
  };
}
